import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { IPdfTemplate } from '../../types/pdfTemplateTypes';
import { TemplateValidationIssue } from './pdfDesignerUtils';

export type PdfSettingsDialog = 'template' | 'page' | 'validation' | 'shortcuts' | null;

interface PdfSettingsDialogsProps {
  dialog: PdfSettingsDialog;
  currentTemplate: IPdfTemplate;
  contentWidthMm: number;
  contentHeightMm: number;
  previewIsStale: boolean;
  previewUrl: string | null;
  validationErrors: TemplateValidationIssue[];
  validationWarnings: TemplateValidationIssue[];
  onClose: () => void;
  onChangeTemplate: React.Dispatch<React.SetStateAction<IPdfTemplate>>;
}

const PdfSettingsDialogs: React.FC<PdfSettingsDialogsProps> = ({
  dialog,
  currentTemplate,
  contentWidthMm,
  contentHeightMm,
  previewIsStale,
  previewUrl,
  validationErrors,
  validationWarnings,
  onClose,
  onChangeTemplate,
}) => {
  const updateMargin = (key: keyof IPdfTemplate['page']['marginMm'], value: number) => {
    onChangeTemplate((prev) => ({
      ...prev,
      page: {
        ...prev.page,
        marginMm: {
          ...prev.page.marginMm,
          [key]: Math.max(0, value || 0),
        },
      },
    }));
  };

  return (
    <>
      <Dialog open={dialog === 'template'} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Mallinställningar</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            size="small"
            label="Mallnamn"
            value={currentTemplate.name}
            onChange={(e) => onChangeTemplate((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
          />
          <TextField
            size="small"
            label="Beskrivning"
            value={currentTemplate.description || ''}
            onChange={(e) => onChangeTemplate((prev) => ({ ...prev, description: e.target.value }))}
            multiline
            minRows={3}
            fullWidth
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={currentTemplate.isActive !== false}
                  onChange={(e) => onChangeTemplate((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Aktiv"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={currentTemplate.showInAssetMenus !== false}
                  onChange={(e) => onChangeTemplate((prev) => ({ ...prev, showInAssetMenus: e.target.checked }))}
                />
              }
              label="Visa i asset-menyer"
            />
          </Box>
          <TextField
            size="small"
            label="Sorteringsordning"
            type="number"
            value={currentTemplate.order ?? 0}
            onChange={(e) => onChangeTemplate((prev) => ({ ...prev, order: Number(e.target.value) || 0 }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Stäng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'page'} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Sidinställningar</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            size="small"
            type="color"
            label="Sidbakgrund"
            value={currentTemplate.page.backgroundColor || '#ffffff'}
            onChange={(e) => onChangeTemplate((prev) => ({
              ...prev,
              page: { ...prev.page, backgroundColor: e.target.value },
            }))}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField size="small" label="Marginal topp (mm)" type="number" value={currentTemplate.page.marginMm.top} onChange={(e) => updateMargin('top', Number(e.target.value))} />
            <TextField size="small" label="Marginal höger (mm)" type="number" value={currentTemplate.page.marginMm.right} onChange={(e) => updateMargin('right', Number(e.target.value))} />
            <TextField size="small" label="Marginal botten (mm)" type="number" value={currentTemplate.page.marginMm.bottom} onChange={(e) => updateMargin('bottom', Number(e.target.value))} />
            <TextField size="small" label="Marginal vänster (mm)" type="number" value={currentTemplate.page.marginMm.left} onChange={(e) => updateMargin('left', Number(e.target.value))} />
          </Box>
          <Alert severity="info">
            Marginalerna definierar innehållsytan. X/Y for element och produktplatser mäts från innehållsytans övre vänstra hörn.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Stäng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'validation'} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Status och validering</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip size="small" label={`${currentTemplate.elements.length} element`} />
            <Chip size="small" label={`Innehållsyta ${Math.round(contentWidthMm * 10) / 10} x ${Math.round(contentHeightMm * 10) / 10} mm`} variant="outlined" />
            <Chip size="small" label={currentTemplate.templateMode === 'multi' ? 'Multi' : '1 produkt'} variant="outlined" />
          </Box>
          {previewIsStale && <Alert severity="warning">Previewn är äldre än arbetsytan. Bygg om den i previewpanelen.</Alert>}
          {!previewIsStale && previewUrl && <Alert severity="success">Previewn matchar nuvarande arbetsyta.</Alert>}
          {currentTemplate.templateMode === 'multi' && (
            <Alert severity="info">Produktdata på sidnivå används inte i multi-export. Lägg produktfält och produktbilder i produktblock.</Alert>
          )}
          {validationErrors.map((issue) => <Alert key={issue.code + issue.message} severity="error">{issue.message}</Alert>)}
          {validationWarnings.map((issue) => <Alert key={issue.code + issue.message} severity="warning">{issue.message}</Alert>)}
          {validationErrors.length === 0 && validationWarnings.length === 0 && (
            <Typography variant="body2" color="text.secondary">Inga valideringsproblem hittades.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Stäng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'shortcuts'} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Kortkommandon</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 1, m: 0 }}>
            <Typography component="dt" fontWeight={700}>Ctrl+S</Typography><Typography component="dd" sx={{ m: 0 }}>Spara mall</Typography>
            <Typography component="dt" fontWeight={700}>Ctrl+D</Typography><Typography component="dd" sx={{ m: 0 }}>Duplicera markerat element</Typography>
            <Typography component="dt" fontWeight={700}>Ctrl+Z / Ctrl+Y</Typography><Typography component="dd" sx={{ m: 0 }}>Ångra och gör om</Typography>
            <Typography component="dt" fontWeight={700}>Piltangenter</Typography><Typography component="dd" sx={{ m: 0 }}>Flytta markerat element eller produktplats</Typography>
            <Typography component="dt" fontWeight={700}>Shift + piltangent</Typography><Typography component="dd" sx={{ m: 0 }}>Flytta i större steg</Typography>
            <Typography component="dt" fontWeight={700}>Alt</Typography><Typography component="dd" sx={{ m: 0 }}>Tillfälligt friare placering utan snap</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PdfSettingsDialogs;
